"use client";
import { useTranslations } from "next-intl";
import type { ComponentPropsWithoutRef, HTMLAttributes } from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import { Textarea } from "../textarea";

// ----------------------------------------------------------------------

interface CustomRHFTextareaProps<TFieldValues extends FieldValues>
  extends Omit<
      ComponentPropsWithoutRef<typeof Controller>,
      "name" | "control" | "render"
    >,
    Omit<ComponentPropsWithoutRef<typeof Textarea>, "name" | "defaultValue"> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: Pick<HTMLAttributes<HTMLDivElement>, "className">;
  loading?: boolean;
  maxLength?: number;
}

export default function CustomRHFTextarea<TFieldValues extends FieldValues>({
  name,
  helperText,
  defaultValue,
  rules,
  containerClass,
  className,
  loading,
  maxLength,
  onChange,
  onBlur,
  ...other
}: CustomRHFTextareaProps<TFieldValues>) {
  const { control } = useFormContext();
  const t = useTranslations("common.form");

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const currentLength = (field.value || "").length;
        const showCharCount = maxLength !== undefined;

        return (
          <div className={cn("space-y-2 w-full", containerClass)}>
            <Textarea
              {...field}
              value={field.value ?? ""}
              {...other}
              browserOwned
              onChange={(event) => {
                field.onChange(event);
                onChange?.(event);
              }}
              onBlur={(event) => {
                field.onBlur();
                onBlur?.(event);
              }}
              disabled={loading}
              maxLength={maxLength}
              className={cn(className, {
                "border-semantic-error": !!error,
              })}
            />
            <div className="flex items-center justify-between">
              <div className="space-y-0 py-1">
                {!!helperText && (
                  <p className="text-xs leading-tight text-muted-foreground ml-1.5">
                    {helperText}
                  </p>
                )}
                {!!error && (
                  <p className="text-xs leading-tight text-semantic-error ml-1.5">
                    {error?.message}
                  </p>
                )}
                {showCharCount && (
                  <p
                    className={cn(
                      "text-xs leading-tight ms-1.5",
                      currentLength > maxLength
                        ? "text-semantic-error"
                        : "text-text-disabled",
                    )}
                  >
                    {currentLength}/{maxLength} {t("charCount")}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}
